/* eslint-disable jsdoc/require-param-type */
/* eslint-disable jsdoc/require-param-description */
import React, { useState, useEffect } from "react";
import {
  Button,
  Grid,
  TextField,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem,
  CircularProgress,
  Box,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  banks,
  bankAccountTypes,
} from "../../../../../components/shared/constants";

const useStyles = makeStyles(theme => ({
  formContainer: {
    marginTop: theme.spacing(2),
  },
  radioGroup: {
    flexDirection: "row",
  },
  buttons: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(3),
  },
  infoText: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 500,
  },
  loadingButton: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
}));

/**
 * BankingInfoForm component with complete banking fields
 * @param root0
 * @param root0.formData
 * @param root0.onBack
 * @param root0.onNext
 * @param root0.submitLabel
 * @param root0.loading
 * @param root0.onSave
 */
export default function BankingInfoForm({
  formData = {},
  onBack,
  onNext,
  submitLabel = "Continue",
  loading = false,
  onSave,
}) {
  const classes = useStyles();

  // Initialize state with formData or defaults
  const [values, setValues] = useState({
    refsPaymentType: formData.refsPaymentType || "bank",
    bank: formData.bank || "",
    refsBankAccountType: formData.refsBankAccountType || "",
    bankAccountNumber: formData.bankAccountNumber || "",
    branchCode: formData.branchCode || "",
    eWalletLink: formData.eWalletLink || "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    setValues({
      refsPaymentType: formData.refsPaymentType || "bank",
      bank: formData.bank || "",
      refsBankAccountType: formData.refsBankAccountType || "",
      bankAccountNumber: formData.bankAccountNumber || "",
      branchCode: formData.branchCode || "",
      eWalletLink: formData.eWalletLink || "",
    });
  }, [formData]);

  // Handle input changes
  const handleChange = field => event => {
    const { value } = event.target;
    const newValues = { ...values, [field]: value };

    // Handle bank selection - auto-fill branch code
    if (field === "bank") {
      const selectedBank = banks.find(bank => bank.value === value);
      if (selectedBank && selectedBank.branchCode) {
        newValues.branchCode = selectedBank.branchCode;
      }
    }

    setValues(newValues);

    if (onSave) {
      const dataToSave = {
        ...formData,
        ...newValues,
      };
      onSave(dataToSave);
    }

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  // Handle account number changes (numeric only)
  const handleAccountNumberChange = event => {
    const numericValue = event.target.value.replace(/[^0-9]/g, "");
    const newValues = { ...values, bankAccountNumber: numericValue };

    setValues(newValues);

    if (onSave) {
      const dataToSave = {
        ...formData,
        ...newValues,
      };
      onSave(dataToSave);
    }

    // Clear error
    if (errors.bankAccountNumber) {
      setErrors(prev => ({ ...prev, bankAccountNumber: "" }));
    }
  };

  // Basic validation
  const validateForm = () => {
    const newErrors = {};

    if (values.refsPaymentType === "bank") {
      if (!values.bank) newErrors.bank = "Bank is required";
      if (!values.refsBankAccountType) {
        newErrors.refsBankAccountType = "Account type is required";
      }
      if (!values.bankAccountNumber?.trim()) {
        newErrors.bankAccountNumber = "Account number is required";
      }
      if (!values.branchCode?.trim()) {
        newErrors.branchCode = "Branch code is required";
      }

      // Basic account number validation
      if (values.bankAccountNumber && values.bankAccountNumber.length < 6) {
        newErrors.bankAccountNumber =
          "Account number must be at least 6 digits";
      }
    }

    return newErrors;
  };

  // Handle form submission
  const handleSubmit = e => {
    e.preventDefault();

    // Clear previous errors
    setErrors({});

    // Basic validation
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const data = {
      ...formData, // Keep all existing form data
      ...values, // Override with banking info changes
    };

    if (onNext) {
      onNext(data);
    }
  };

  const handleBackClick = () => {
    if (onBack) {
      // Save current changes before going back
      const dataToSave = {
        ...formData,
        ...values,
      };
      onBack(dataToSave);
    }
  };

  return (
    <form className={classes.formContainer} onSubmit={handleSubmit}>
      <Typography variant="h6" gutterBottom className={classes.sectionTitle}>
        Payment Information
      </Typography>

      <Grid container spacing={3}>
        {/* Payment Type */}
        <Grid item xs={12}>
          <FormControl component="fieldset" required>
            <FormLabel component="legend">Payment Type</FormLabel>
            <RadioGroup
              value={values.refsPaymentType}
              onChange={handleChange("refsPaymentType")}
              className={classes.radioGroup}
            >
              <FormControlLabel
                value="bank"
                control={<Radio />}
                label="Bank Account"
              />
              <FormControlLabel
                value="eWallet"
                control={<Radio />}
                label="eWallet"
              />
            </RadioGroup>
          </FormControl>
        </Grid>

        {/* Bank Account Fields */}
        {values.refsPaymentType === "bank" && (
          <>
            {/* Bank Selection */}
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Bank"
                variant="outlined"
                fullWidth
                required
                value={values.bank}
                onChange={handleChange("bank")}
                error={!!errors.bank}
                helperText={errors.bank}
              >
                <MenuItem value="">
                  <em>Select a bank</em>
                </MenuItem>
                {banks.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Account Type */}
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Account Type"
                variant="outlined"
                fullWidth
                required
                value={values.refsBankAccountType}
                onChange={handleChange("refsBankAccountType")}
                error={!!errors.refsBankAccountType}
                helperText={errors.refsBankAccountType}
              >
                <MenuItem value="">
                  <em>Select account type</em>
                </MenuItem>
                {bankAccountTypes.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Account Number */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Account Number"
                variant="outlined"
                fullWidth
                required
                value={values.bankAccountNumber}
                onChange={handleAccountNumberChange}
                error={!!errors.bankAccountNumber}
                helperText={errors.bankAccountNumber || "Minimum 6 digits"}
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
              />
            </Grid>

            {/* Branch Code */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Branch Code"
                variant="outlined"
                fullWidth
                disabled
                value={values.branchCode}
                error={!!errors.branchCode}
                helperText={
                  values.branchCode
                    ? "Auto-filled based on selected bank"
                    : errors.branchCode ||
                      "Will be auto-filled when bank is selected"
                }
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
          </>
        )}

        {/* eWallet Fields */}
        {values.refsPaymentType === "eWallet" && (
          <>
            <Grid item xs={12}>
              <Typography className={classes.infoText}>
                We will use the primary phone number{" "}
                <strong>{formData.phone || "[not provided yet]"}</strong> for
                eWallet payments.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="eWallet Link (optional)"
                variant="outlined"
                fullWidth
                value={values.eWalletLink}
                onChange={handleChange("eWalletLink")}
                error={!!errors.eWalletLink}
                helperText={
                  errors.eWalletLink ||
                  "Optional: Provide if you have a specific eWallet link"
                }
              />
            </Grid>
          </>
        )}
      </Grid>

      <div className={classes.buttons}>
        <Button onClick={handleBackClick} disabled={!onBack || loading}>
          Back
        </Button>
        <Button
          variant="contained"
          color="primary"
          type="submit"
          disabled={loading}
          className={classes.loadingButton}
        >
          {loading ? (
            <Box className={classes.loadingButton}>
              <CircularProgress size={20} />
              Saving...
            </Box>
          ) : (
            submitLabel
          )}
        </Button>
      </div>
    </form>
  );
}
